/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

/**
 * Safe wrapper for Clerk's useSession hook that works in test mode
 *
 * This hook always calls Clerk's useSession unconditionally to comply with
 * React's rules of hooks, but returns mock values when test mode is active.
 */

import { useSession as useClerkSession } from "@clerk/nextjs";

// Test mode detection
const isTestModeActive = (): boolean => {
  const envTestMode =
    process.env["NEXT_PUBLIC_E2E_TEST_MODE"] === "true" ||
    process.env["E2E_TEST_MODE"] === "true" ||
    process.env["NODE_ENV"] === "test";

  if (typeof window !== "undefined") {
    return envTestMode || Boolean(window.__TEST_BYPASS_ENABLED);
  }

  return envTestMode;
};

/**
 * Safe wrapper for useSession that works in test mode
 */
export function useSessionSafe(): ReturnType<typeof useClerkSession> {
  // Always call hooks unconditionally to comply with rules of hooks
  const clerkSession = useClerkSession();
  const testModeActive = isTestModeActive();

  if (testModeActive) {
    // Type assertion is safe here - we're providing a test implementation that satisfies the interface
    return {
      isLoaded: true,
      session: {
        id: "test-session-id",
        getToken: async () => "test-token",
      },
    } as ReturnType<typeof useClerkSession>;
  }

  // In normal mode, return Clerk's useSession result
  return clerkSession;
}
